import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  inject,
} from '@angular/core';
import { AccountService } from '@wilson/account';
import {
  MonthlyClosureStatusEnum,
  UserPayrollMonthlyClosure,
} from '@wilson/interfaces';
import { DateTimeFormat } from '@wilson/utils';
import { format } from 'date-fns';
import { map, Observable, of, tap } from 'rxjs';
import { BaseSingleSelectFilterDirective } from '../../base-single-select-filter.directive';
import { FilterService } from '../../filter.service';
import { StringParserService } from '../../services/string-parser.service';

export interface UsersFilterOptions {
  id: string;
  name: string;
  status: MonthlyClosureStatusEnum | null;
}
const FILTER_KEY = 'userId';
@Component({
  selector: 'wilson-user-with-monthly-closures-status-filter',
  templateUrl: './user-with-monthly-closures-status-filter.component.html',
  styleUrls: ['./user-with-monthly-closures-status-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class UserWithMonthlyClosuresStatusFilterComponent
  extends BaseSingleSelectFilterDirective<UsersFilterOptions, string>
  implements OnChanges
{
  protected readonly filterService = inject(FilterService);
  protected readonly parserService =
    inject<StringParserService<string>>(StringParserService);
  protected readonly accountService = inject(AccountService);

  @Input() showLabel = false;
  @Input() label = null;
  @Input({ required: true }) selectedMonth!: Date;
  @Input() isSetDefaultUserId = false;
  @Input() refresh!: Date;
  @Input() hasUserReadPermissions!: boolean;

  protected nzDropdownStyle = {
    'min-width': '20rem',
  };
  private users!: UsersFilterOptions[];

  constructor() {
    super(FILTER_KEY);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['selectedMonth'] || changes['refresh']) &&
      !this.isSetDefaultUserId
    ) {
      this.options$ = this.initializeOptions();
    }
  }

  protected initializeOptions(): Observable<UsersFilterOptions[]> {
    if (this.hasUserReadPermissions) {
      return this.accountService
        .getUsers([], {}, false, 'userPayrollMonthlyClosures')
        .pipe(
          map((users) => {
            users.sort((a, b) => a.lastName.localeCompare(b.lastName));
            return users.map((user) => {
              return {
                id: user.id as string,
                name: `${user.lastName}, ${user.firstName}`,
                status: this.getUserPayrollMonthlyClosuresStatus(
                  user.userPayrollMonthlyClosures || [],
                ),
              };
            });
          }),
          tap((users) => {
            this.users = users;
            if (this.isSetDefaultUserId) {
              this.filterService.saveValueInQueryParam(
                FILTER_KEY,
                this.parserService.parse(this.users[0].id),
              );
            }
          }),
        );
    } else {
      return of([]);
    }
  }

  private getUserPayrollMonthlyClosuresStatus(
    userPayrollMonthlyClosures: UserPayrollMonthlyClosure[],
  ): MonthlyClosureStatusEnum | null {
    const currentMonthClosure = userPayrollMonthlyClosures.find(
      (closure) =>
        closure.yearAndMonth ===
        format(this.selectedMonth, DateTimeFormat.MonthFormat),
    );

    return currentMonthClosure ? currentMonthClosure.status : null;
  }
}
